/**
 * The tracking API components call. Nothing here knows about GTM tags — it only
 * produces the dataLayer contract the container reads from.
 */

import {
  CURRENCY,
  Ecommerce,
  TrackingItem,
  money,
  newEventId,
  pushEcommerce,
  pushEvent,
} from "./dataLayer";
import { UserData, buildUserData } from "./hash";
import {
  PendingPurchase,
  clearActiveCheckout,
  getActiveCheckout,
  hasOrderEventFired,
  hasPurchaseFired,
  markOrderEventFired,
  markPurchaseFired,
  savePendingPurchase,
  startCheckout,
  takePendingPurchase,
  updateActiveCheckout,
} from "./checkoutSession";

/**
 * SPA route changes need a manual push — GTM's History Change trigger is
 * unreliable on client-side routers.
 */
export const trackPageView = (title?: string) => {
  pushEvent("page_view", {
    page_location: window.location.href,
    page_path: window.location.pathname + window.location.search,
    page_title: title || document.title,
  });
};

export const trackViewItemList = (args: {
  items: TrackingItem[];
  listId: string;
  listName: string;
}) => {
  if (!args.items.length) return;
  pushEcommerce(
    "view_item_list",
    { items: args.items },
    { item_list_id: args.listId, item_list_name: args.listName }
  );
};

/** Filter/sort changes re-render the same list, so they carry the same list id. */
export const trackFilterApplied = (args: {
  listId: string;
  filters: Record<string, any>;
  resultCount: number;
}) => {
  pushEvent("filter_applied", {
    item_list_id: args.listId,
    filters: args.filters,
    result_count: args.resultCount,
  });
};

export const trackSelectItem = (item: TrackingItem, listId: string) => {
  pushEcommerce("select_item", { items: [item] }, { item_list_id: listId });
};

export const trackAddToCompare = (item: TrackingItem) => {
  pushEcommerce("add_to_compare", { items: [item] });
};

export const trackViewItem = (item: TrackingItem) => {
  pushEcommerce("view_item", {
    currency: CURRENCY,
    value: item.price,
    items: [item],
  });
};

/**
 * Fires on the Buy / port-in CTA. The order id does not exist yet, so a local
 * `checkout_id` is minted here and carried until the backend mints the real
 * one. `value` is the plan price — the charged total (which adds the one-time
 * service fee) is only known after the checkout call, at add_payment_info.
 */
export const trackBeginCheckout = (args: {
  item: TrackingItem;
  numberType?: "new" | "port_in";
}) => {
  const checkoutId = newEventId();
  startCheckout({ checkout_id: checkoutId, item: args.item });
  pushEcommerce(
    "begin_checkout",
    { currency: CURRENCY, value: args.item.price, items: [args.item] },
    { checkout_id: checkoutId, number_type: args.numberType || "new" }
  );
};

export type CheckoutStep =
  | "number_selection"
  | "personal_info"
  | "otp_verification"
  | "payment";

export const trackCheckoutProgress = (
  step: CheckoutStep,
  extra: Record<string, any> = {}
) => {
  const active = getActiveCheckout();
  pushEvent("checkout_progress", {
    checkout_step: step,
    checkout_id: active?.checkout_id,
    ...extra,
  });
};

/**
 * Number reserved or port-in submitted. Distinct from the checkout_progress
 * event for the same step: that one fires on entry, this on completion. The
 * spec maps it to add_shipping_info on the GTM side — it is the highest
 * drop-off point in a telecom funnel and the most actionable one to optimise.
 */
export const trackNumberReserved = (numberType: "new" | "port_in") => {
  const active = getActiveCheckout();
  pushEvent("number_reserved", {
    number_type: numberType,
    checkout_id: active?.checkout_id,
  });
};

/**
 * Personal-info/OTP step. This is where the hashed match parameters are built,
 * once, and stashed for the purchase event that fires after the gateway
 * redirect (by which point the raw values are long gone).
 */
export const trackSignUp = async (args: {
  email?: string;
  countryCode?: string;
  phone?: string;
  method?: string;
  returning?: boolean;
}) => {
  const userData = await buildUserData(args);
  updateActiveCheckout({ user_data: userData });
  pushEvent(args.returning ? "login" : "sign_up", {
    method: args.method || "mobile_otp",
    user_data: userData,
  });
};

/**
 * Fires when a payment method is selected, before the charge. Also snapshots
 * everything the purchase event will need on the other side of the redirect.
 */
export const trackAddPaymentInfo = (args: {
  transactionId: string;
  value: number;
  items: TrackingItem[];
  paymentType: string;
  userData?: UserData;
}) => {
  const active = getActiveCheckout();
  const userData = args.userData || active?.user_data;

  const ecommerce: Ecommerce = {
    currency: CURRENCY,
    value: money(args.value),
    transaction_id: args.transactionId,
    payment_type: args.paymentType,
    items: args.items,
  };
  pushEcommerce("add_payment_info", ecommerce, {
    checkout_id: active?.checkout_id,
  });

  savePendingPurchase({
    transaction_id: args.transactionId,
    checkout_id: active?.checkout_id,
    value: money(args.value),
    currency: CURRENCY,
    payment_type: args.paymentType,
    items: args.items,
    user_data: userData,
  });
};

/**
 * Fires once per order, only after the backend confirms the payment. The
 * `event_id` is the order id so the payment webhook can reproduce it — Meta,
 * TikTok and Snap all dedupe browser against server on that value, and a
 * mismatch double-counts every conversion.
 *
 * Returns false when the order has already been tracked (page reload).
 */
export const trackPurchase = (purchase: PendingPurchase): boolean => {
  if (!purchase.transaction_id) return false;
  if (hasPurchaseFired(purchase.transaction_id)) return false;
  markPurchaseFired(purchase.transaction_id);

  pushEcommerce(
    "purchase",
    {
      transaction_id: purchase.transaction_id,
      currency: purchase.currency || CURRENCY,
      value: money(purchase.value),
      payment_type: purchase.payment_type,
      items: purchase.items.map((item) => ({ quantity: 1, ...item })),
    },
    {
      event_id: purchase.transaction_id,
      checkout_id: purchase.checkout_id,
      user_data: purchase.user_data,
    }
  );
  clearActiveCheckout();
  return true;
};

/**
 * Recovers the snapshot taken at add_payment_info and fires the purchase.
 * `fallback` fills in from URL params when the snapshot is missing (payment
 * finished in another tab, storage blocked) — without it the conversion is lost
 * entirely, with it we at least report value and order id.
 */
export const trackPurchaseForOrder = (
  transactionId?: string | null,
  fallback?: Partial<PendingPurchase>
): boolean => {
  if (!transactionId) return false;
  const pending = takePendingPurchase(transactionId);
  if (!pending && !fallback) return false;

  return trackPurchase({
    transaction_id: transactionId,
    currency: CURRENCY,
    value: 0,
    items: [],
    ...(fallback || {}),
    ...(pending || {}),
  } as PendingPurchase);
};

/**
 * Flat companion to the GA4 `purchase` event, for tags that read plain Data
 * Layer Variables (`order_id`, `value`, `package`) instead of the nested
 * `ecommerce` object.
 *
 * Deliberately NOT named `purchase`: a second event under that name would fire
 * every purchase-triggered tag twice and double-count revenue on GA4, Meta,
 * TikTok and Snap.
 *
 * Call this BEFORE trackPurchaseForOrder. That one clears the active checkout,
 * which is where the package name is read from once the gateway redirect has
 * wiped React state.
 *
 * Returns false when this order already fired (page reload).
 */
export const PURCHASE_ORDER_EVENT = "purchase_order";

export const trackPurchaseOrder = (args: {
  orderId?: string | null;
  value: number;
  packageName?: string;
  packageId?: string | null;
}): boolean => {
  if (!args.orderId) return false;
  if (hasOrderEventFired(PURCHASE_ORDER_EVENT, args.orderId)) return false;
  markOrderEventFired(PURCHASE_ORDER_EVENT, args.orderId);

  const item = getActiveCheckout()?.item;
  const packageName = args.packageName || item?.item_name;
  const packageId = args.packageId || item?.item_id;

  const payload: Record<string, any> = {
    event_id: args.orderId,
    order_id: args.orderId,
    value: money(args.value),
    currency: CURRENCY,
  };
  if (packageName) payload.package = packageName;
  if (packageId) payload.package_id = String(packageId);

  pushEvent(PURCHASE_ORDER_EVENT, payload);
  return true;
};

/**
 * Home routers take no payment — the flow ends at a subscription request, so it
 * is a lead. Sending it as a purchase would book revenue that was never
 * collected and pull budget toward the campaign with the least real revenue.
 */
export const trackGenerateLead = (args: {
  items?: TrackingItem[];
  value?: number;
  leadType: string;
  orderId?: string | null;
}) => {
  pushEcommerce(
    "generate_lead",
    {
      currency: CURRENCY,
      value: money(args.value || 0),
      items: args.items || [],
    },
    { lead_type: args.leadType, order_id: args.orderId || undefined }
  );
};
